// Mock coaching sessions and appointments
import type { Session } from '@/types'

export const mockSessions: Session[] = [
  {
    id: 'session-001',
    clientId: 'client-001',
    coachId: 'coach-001',
    type: 'training',
    
    // Scheduling
    date: new Date('2025-01-26'),
    startTime: '10:00',
    endTime: '11:00',
    duration: 60,
    location: {
      type: 'gym',
      name: 'Fitness Plus Gym',
      address: '123 Main St, Anytown, USA',
      notes: 'Use the free weights area'
    },
    
    // Content
    agenda: [
      'Warm-up and mobility',
      'Squat technique review',
      'Upper body strength work',
      'Cool-down and stretching'
    ],
    goals: [
      'Improve squat depth and form',
      'Increase push-up reps',
      'Address shoulder mobility'
    ],
    
    // Status and completion
    status: 'scheduled',
    
    // Notes and follow-up
    preSessionNotes: 'Sarah mentioned some knee discomfort last week. Check squat form and consider modifications if needed.',
    
    createdAt: new Date('2025-01-24T14:30:00'),
    updatedAt: new Date('2025-01-24T14:30:00')
  },

  {
    id: 'session-002',
    clientId: 'client-003',
    coachId: 'coach-001',
    type: 'consultation',
    
    date: new Date('2025-01-26'),
    startTime: '14:00',
    endTime: '14:45',
    duration: 45,
    location: {
      type: 'virtual',
      name: 'Zoom Meeting',
      notes: 'Initial consultation and goal setting'
    },
    
    agenda: [
      'Get to know Emma',
      'Discuss fitness history and goals',
      'Review program options',
      'Schedule first training session'
    ],
    goals: [
      'Understand Emma\'s fitness background',
      'Set realistic short-term goals',
      'Create initial training plan'
    ],
    
    status: 'scheduled',
    
    preSessionNotes: 'Emma is new to structured fitness training. Focus on building confidence and establishing realistic expectations.',
    
    createdAt: new Date('2025-01-25T16:30:00'),
    updatedAt: new Date('2025-01-25T16:30:00')
  },

  {
    id: 'session-003',
    clientId: 'client-002',
    coachId: 'coach-001',
    type: 'assessment',
    
    date: new Date('2025-01-24'),
    startTime: '09:00',
    endTime: '10:00',
    duration: 60,
    location: {
      type: 'gym',
      name: 'Fitness Plus Gym',
      address: '123 Main St, Anytown, USA'
    },
    
    agenda: [
      'Movement assessment',
      'Strength testing',
      'Program progression review',
      'Goal adjustment discussion'
    ],
    goals: [
      'Evaluate Mike\'s progress',
      'Identify areas for improvement',
      'Adjust program difficulty'
    ],
    
    status: 'completed',
    completedAt: new Date('2025-01-24T10:00:00'),
    
    preSessionNotes: 'Mike has been consistent with his workouts. Time to assess progress and potentially increase difficulty.',
    postSessionNotes: 'Mike showed significant improvement in squat depth and push-up form. Ready to progress to intermediate exercises. Increased confidence noted. Will add pull-up progressions next week.',
    
    clientFeedback: {
      rating: 5,
      difficulty: 7,
      enjoyment: 9,
      energy: 8,
      comments: 'Really helpful session! I can see how much I\'ve improved since starting. Excited about the new exercises.'
    },
    
    followUpActions: [
      'Update program with intermediate exercises',
      'Add pull-up progression work',
      'Schedule next assessment in 4 weeks'
    ],
    
    createdAt: new Date('2025-01-22T10:00:00'),
    updatedAt: new Date('2025-01-24T10:30:00')
  },

  {
    id: 'session-004',
    clientId: 'client-001',
    coachId: 'coach-001',
    type: 'training',
    
    date: new Date('2025-01-22'),
    startTime: '10:00',
    endTime: '11:00',
    duration: 60,
    location: {
      type: 'gym',
      name: 'Fitness Plus Gym',
      address: '123 Main St, Anytown, USA'
    },
    
    agenda: [
      'Dynamic warm-up',
      'Barbell squat progression',
      'Upper body push/pull work',
      'Core strengthening',
      'Flexibility work'
    ],
    goals: [
      'Increase squat weight',
      'Perfect push-up form',
      'Improve core stability'
    ],
    
    status: 'completed',
    completedAt: new Date('2025-01-22T11:00:00'),
    
    preSessionNotes: 'Sarah felt strong last session. Ready to increase squat weight by 5lbs.',
    postSessionNotes: 'Excellent session! Sarah hit all her squat reps at the new weight (145lbs). Push-ups are improving - she completed 3 sets of 12 with perfect form. Core work was challenging but she pushed through. Very motivated and positive attitude.',
    
    clientFeedback: {
      rating: 5,
      difficulty: 8,
      enjoyment: 9,
      energy: 7,
      comments: 'Loved the new squat weight! Feeling so much stronger. The core work was tough but I can feel it working.'
    },
    
    followUpActions: [
      'Continue with current squat progression',
      'Add more challenging push-up variations',
      'Introduce plank progressions'
    ],
    
    createdAt: new Date('2025-01-20T15:00:00'),
    updatedAt: new Date('2025-01-22T11:15:00')
  },

  {
    id: 'session-005',
    clientId: 'client-002',
    coachId: 'coach-001',
    type: 'training',
    
    date: new Date('2025-01-28'),
    startTime: '11:00',
    endTime: '12:00',
    duration: 60,
    location: {
      type: 'gym',
      name: 'Fitness Plus Gym',
      address: '123 Main St, Anytown, USA'
    },
    
    status: 'scheduled',
    
    preSessionNotes: 'Mike completed his assessment well. Today we\'ll introduce some new intermediate exercises including assisted pull-ups.',
    
    createdAt: new Date('2025-01-25T09:00:00'),
    updatedAt: new Date('2025-01-25T09:00:00')
  },

  {
    id: 'session-006',
    clientId: 'client-001',
    coachId: 'coach-001',
    type: 'check-in',
    
    date: new Date('2025-01-29'),
    startTime: '15:30',
    endTime: '16:00',
    duration: 30,
    location: {
      type: 'virtual',
      name: 'Phone Call',
      notes: 'Weekly progress check-in'
    },
    
    agenda: [
      'Review this week\'s workouts',
      'Discuss nutrition and recovery',
      'Address any concerns or questions',
      'Plan next week\'s focus'
    ],
    
    status: 'scheduled',
    
    createdAt: new Date('2025-01-25T12:00:00'),
    updatedAt: new Date('2025-01-25T12:00:00')
  },

  // August 2025 sessions (current calendar view)
  {
    id: 'session-007',
    clientId: 'client-001',
    coachId: 'coach-001',
    type: 'training',
    
    date: new Date('2025-08-05'),
    startTime: '09:00',
    endTime: '10:00',
    duration: 60,
    location: {
      type: 'gym',
      name: 'Fitness Plus Gym',
      address: '123 Main St, Anytown, USA'
    },
    
    agenda: [
      'Full body strength training',
      'Cardio finisher',
      'Flexibility work'
    ],
    goals: [
      'Increase overall strength',
      'Improve cardiovascular fitness'
    ],
    
    status: 'scheduled',
    
    preSessionNotes: 'Sarah is back after vacation. Start with lighter weights and build back up.',
    
    createdAt: new Date('2025-08-01T10:00:00'),
    updatedAt: new Date('2025-08-01T10:00:00')
  },

  {
    id: 'session-008',
    clientId: 'client-002',
    coachId: 'coach-001',
    type: 'consultation',
    
    date: new Date('2025-08-06'),
    startTime: '14:00',
    endTime: '15:00',
    duration: 60,
    location: {
      type: 'virtual',
      name: 'Zoom Meeting'
    },
    
    agenda: [
      'Program review and adjustments',
      'Discuss nutrition goals',
      'Plan next phase'
    ],
    goals: [
      'Evaluate current progress',
      'Set new targets'
    ],
    
    status: 'scheduled',
    
    preSessionNotes: 'Mike has been very consistent. Time to discuss next level goals.',
    
    createdAt: new Date('2025-08-02T09:00:00'),
    updatedAt: new Date('2025-08-02T09:00:00')
  },

  {
    id: 'session-009',
    clientId: 'client-003',
    coachId: 'coach-001',
    type: 'training',
    
    date: new Date('2025-08-07'),
    startTime: '11:00',
    endTime: '12:00',
    duration: 60,
    location: {
      type: 'gym',
      name: 'Fitness Plus Gym',
      address: '123 Main St, Anytown, USA'
    },
    
    agenda: [
      'Lower body focus',
      'Core strengthening',
      'Balance training'
    ],
    goals: [
      'Build leg strength',
      'Improve stability'
    ],
    
    status: 'scheduled',
    
    preSessionNotes: 'Emma is progressing well. Ready for more challenging exercises.',
    
    createdAt: new Date('2025-08-03T15:00:00'),
    updatedAt: new Date('2025-08-03T15:00:00')
  },

  {
    id: 'session-010',
    clientId: 'client-001',
    coachId: 'coach-001',
    type: 'assessment',
    
    date: new Date('2025-08-12'),
    startTime: '10:00',
    endTime: '11:30',
    duration: 90,
    location: {
      type: 'gym',
      name: 'Fitness Plus Gym',
      address: '123 Main St, Anytown, USA'
    },
    
    agenda: [
      'Strength testing',
      'Body composition check',
      'Progress photos',
      'Program planning'
    ],
    goals: [
      'Track progress metrics',
      'Plan next program phase'
    ],
    
    status: 'scheduled',
    
    preSessionNotes: 'Monthly progress assessment. Bring measuring tape and camera.',
    
    createdAt: new Date('2025-08-05T12:00:00'),
    updatedAt: new Date('2025-08-05T12:00:00')
  },

  {
    id: 'session-011',
    clientId: 'client-002',
    coachId: 'coach-001',
    type: 'training',
    
    date: new Date('2025-08-14'),
    startTime: '16:00',
    endTime: '17:00',
    duration: 60,
    location: {
      type: 'outdoor',
      name: 'Central Park',
      notes: 'Meet at the main entrance'
    },
    
    agenda: [
      'Outdoor cardio workout',
      'Bodyweight exercises',
      'Functional movement'
    ],
    goals: [
      'Enjoy nature workout',
      'Build functional strength'
    ],
    
    status: 'scheduled',
    
    preSessionNotes: 'Weather permitting - outdoor session. Bring water bottle.',
    
    createdAt: new Date('2025-08-07T11:00:00'),
    updatedAt: new Date('2025-08-07T11:00:00')
  }
]

// Helper functions for session data
export const getUpcomingSessions = (coachId: string, days: number = 7): Session[] => {
  const now = new Date()
  const futureDate = new Date(now.getTime() + (days * 24 * 60 * 60 * 1000))
  
  return mockSessions.filter(session => 
    session.coachId === coachId &&
    session.status === 'scheduled' &&
    session.date >= now &&
    session.date <= futureDate
  ).sort((a, b) => a.date.getTime() - b.date.getTime())
}

export const getTodaysSessions = (coachId: string): Session[] => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000)
  
  return mockSessions.filter(session => 
    session.coachId === coachId &&
    session.date >= today &&
    session.date < tomorrow
  ).sort((a, b) => a.startTime.localeCompare(b.startTime))
}

export const getSessionsByClient = (clientId: string): Session[] => {
  return mockSessions.filter(session => session.clientId === clientId)
    .sort((a, b) => b.date.getTime() - a.date.getTime())
}

export const getCompletedSessions = (coachId: string, limit?: number): Session[] => {
  const completed = mockSessions.filter(session => 
    session.coachId === coachId && session.status === 'completed'
  ).sort((a, b) => b.date.getTime() - a.date.getTime())
  
  return limit ? completed.slice(0, limit) : completed
}

export const getSessionsByDateRange = (
  coachId: string, 
  startDate: Date, 
  endDate: Date
): Session[] => {
  return mockSessions.filter(session => 
    session.coachId === coachId &&
    session.date >= startDate &&
    session.date <= endDate
  ).sort((a, b) => a.date.getTime() - b.date.getTime())
}